import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router";
import { toast } from "sonner";
import { api } from "@/api";
import { getBuyerAuthToken } from "@/api/token";
import { AssistantLauncher } from "@/components/assistant/assistant-launcher";
import { Toaster } from "@/components/ui/sonner";
import { useRegionSync } from "@/lib/use-region";
import { useCart, useWishlist } from "@/store";
import { attachDeviceToAccount, expireStaleGuestDevice } from "@/store/device";
import { setWishlistErrorReporter } from "@/store/wishlist";
import { CookieConsent } from "./cookie-consent";
import { MobileTabBar } from "./mobile-tab-bar";
import { SiteFooter } from "./site-footer";
import { SiteHeader } from "./site-header";

// App shell: persistent header + footer wrap the routed page (<Outlet />).
// On first mount it re-hydrates the persisted cart from the gateway so the
// header badge and /cart are correct after a reload. Scrolls to top on every
// navigation so deep pages don't open mid-scroll.
export function SiteLayout() {
  const $ = _c(17);
  const {
    pathname
  } = useLocation();
  const {
    t
  } = useTranslation();
  let t0;
  let t1;
  if ($[0] !== t) {
    t0 = () => {
      setWishlistErrorReporter(() => toast.error(t("wishlist.saveError")));
      return _temp2;
    };
    t1 = [t];
    $[0] = t;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  useRegionSync();
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(_temp5, t2);
  let t3;
  if ($[4] !== pathname) {
    t3 = [pathname];
    $[4] = pathname;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(_temp6, t3);
  const overlayHeader = pathname === "/" || pathname === "/sell";
  const lightLogo = pathname === "/sell";
  let t4;
  if ($[6] !== lightLogo || $[7] !== overlayHeader) {
    t4 = <SiteHeader overlay={overlayHeader} lightLogo={lightLogo} />;
    $[6] = lightLogo;
    $[7] = overlayHeader;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <main className="mx-auto w-full max-w-[1440px] flex-1 px-4 pt-2.5 pb-6 md:px-8 md:pb-8"><Outlet /></main>;
    t6 = <SiteFooter />;
    t7 = <MobileTabBar />;
    t8 = <AssistantLauncher />;
    t9 = <CookieConsent />;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Toaster position="top-center" richColors={true} offset={{
      top: 84,
      right: 24,
      left: 24
    }} mobileOffset={{
      top: 80,
      right: 16,
      left: 16
    }} />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t4) {
    t11 = <div className="flex min-h-screen flex-col bg-background pb-[calc(67px+env(safe-area-inset-bottom))] lg:pb-0">{t4}{t5}{t6}{t7}{t8}{t9}{t10}</div>;
    $[15] = t4;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  return t11;
}
function _temp6() {
  window.scrollTo(0, 0);
}
function _temp5() {
  _temp4();
}
async function _temp4() {
  expireStaleGuestDevice();
  if (getBuyerAuthToken()) {
    await attachDeviceToAccount();
  } else {
    await useCart.getState().refresh();
  }
  try {
    await useWishlist.getState().syncFromServer();
    const ids = useWishlist.getState().ids;
    if (ids.length === 0) {
      return;
    }
    const products = await api.products.byIds(ids);
    useWishlist.getState().pruneProducts(products.map(_temp3));
  } catch {}
}
function _temp3(p) {
  return p.id;
}
function _temp2() {
  return setWishlistErrorReporter(_temp);
}
function _temp() {}